import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';

const DatePicker = ({ value, onChange, placeholder, name }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(value || '');
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        setSelectedDate(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;
            
            // DatePicker container'ına tıklanmadıysa
            if (containerRef.current && !containerRef.current.contains(target)) {
                // Modal backdrop'una tıklanırsa ignore et (modal backdrop'u fixed ve bg-black bg-opacity-50 class'larına sahip)
                const isBackdrop = target.classList.contains('bg-black') && 
                                   target.classList.contains('bg-opacity-50') &&
                                   target.classList.contains('fixed');
                
                // Modal içeriğine tıklanırsa ignore et
                const isModalContent = target.closest('[class*="z-10"]') || 
                                      target.closest('[class*="z-[9999]"]') ||
                                      target.closest('[class*="modal"]');
                
                // Eğer backdrop veya modal içeriğine tıklanmadıysa kapat
                if (!isBackdrop && !isModalContent) {
                    setIsOpen(false);
                }
            }
        };

        // Sadece calendar açıkken event listener ekle
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isOpen]);

    const generateCalendar = () => {
        const firstDay = new Date(currentYear, currentMonth, 1);
        const lastDay = new Date(currentYear, currentMonth + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(day);
        }

        return days;
    };

    const handleDateSelect = (day) => {
        if (day) {
            // Timezone sorunlarını önlemek için doğrudan string formatında tarih oluştur
            const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            setSelectedDate(formattedDate);
            onChange({ target: { value: formattedDate } });
            setIsOpen(false);
        }
    };

    const handlePrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const formatDisplayDate = (dateString) => {
        if (!dateString) return '';
        // Doğrudan string parsing ile timezone sorunlarını önle
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    };

    const days = generateCalendar();
    const monthNames = [
        'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];

    return (
        <div className="relative" ref={containerRef}>
            <div
                className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-600 dark:border-gray-600 rounded-md bg-gray-700 dark:bg-gray-700 text-white dark:text-white cursor-pointer flex items-center justify-between"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                ref={inputRef}
            >
                <span className={selectedDate ? 'text-white dark:text-white' : 'text-gray-400'}>
                    {selectedDate ? formatDisplayDate(selectedDate) : placeholder}
                </span>
                <CalendarIcon className="w-4 h-4 text-gray-400" />
            </div>

            {isOpen && (
                <div 
                    className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-[10000] p-2 min-w-[200px]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between mb-2">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePrevMonth();
                            }}
                            className="text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                        >
                            ‹
                        </button>
                        <div className="text-center text-xs font-medium text-gray-900 dark:text-white">
                            {monthNames[currentMonth]} {currentYear}
                        </div>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNextMonth();
                            }}
                            className="text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded"
                        >
                            ›
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-0.5 text-center text-xs mb-1">
                        {['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'].map(day => (
                            <div key={day} className="font-medium text-gray-500 dark:text-gray-400 p-0.5">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-0.5">
                        {days.map((day, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDateSelect(day);
                                }}
                                disabled={!day}
                                className={`
                  p-0.5 text-xs rounded transition-colors
                  ${!day ? 'invisible' : ''}
                  ${day ? 'hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-900 dark:text-white' : ''}
                  ${selectedDate && parseInt(selectedDate.split('-')[2]) === day ? 'bg-blue-600 text-white' : ''}
                `}
                            >
                                {day}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
